import { useEffect, useMemo, useRef } from "react";
import "./otp-input.css";

const OTP_LENGTH = 6;

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
}

export default function OtpInput({
  value = "",
  onChange,
  onComplete,
  error = false,
  disabled = false,
  autoFocus = true,
  ariaLabel = "6-digit verification code",
  className = "",
}) {
  const inputRefs = useRef([]);
  const initialFocusDoneRef = useRef(false);
  const previousErrorRef = useRef(error);
  const code = useMemo(() => digitsOnly(value), [value]);

  const focusBox = (index) => {
    const boundedIndex = Math.max(0, Math.min(OTP_LENGTH - 1, index));
    inputRefs.current[boundedIndex]?.focus();
  };

  const commit = (nextCode, nextFocusIndex) => {
    const normalized = digitsOnly(nextCode);
    const becameComplete = (
      code.length < OTP_LENGTH && normalized.length === OTP_LENGTH
    );
    onChange?.(normalized);
    if (becameComplete) {
      onComplete?.(normalized);
    }
    if (Number.isInteger(nextFocusIndex)) {
      focusBox(nextFocusIndex);
    }
  };

  useEffect(() => {
    const becameResetError = error && !previousErrorRef.current && code.length === 0;
    previousErrorRef.current = error;

    if (disabled) {
      return;
    }

    if (becameResetError || (autoFocus && !initialFocusDoneRef.current)) {
      focusBox(0);
      initialFocusDoneRef.current = true;
    }
  }, [autoFocus, code.length, disabled, error]);

  const handleChange = (index, event) => {
    const entered = digitsOnly(event.target.value);
    if (!entered) {
      return;
    }

    if (entered.length === OTP_LENGTH) {
      commit(entered, OTP_LENGTH - 1);
      return;
    }

    const targetIndex = Math.min(index, code.length);
    const next = `${code.slice(0, targetIndex)}${entered[entered.length - 1]}${code.slice(targetIndex + 1)}`;
    commit(next, Math.min(targetIndex + 1, OTP_LENGTH - 1));
  };

  const handlePaste = (index, event) => {
    event.preventDefault();
    const pasted = digitsOnly(event.clipboardData?.getData("text/plain"));
    if (!pasted) {
      return;
    }

    if (pasted.length === OTP_LENGTH) {
      commit(pasted, OTP_LENGTH - 1);
      return;
    }

    const targetIndex = Math.min(index, code.length);
    const characters = code.split("");
    pasted.split("").forEach((digit, offset) => {
      if (targetIndex + offset < OTP_LENGTH) {
        characters[targetIndex + offset] = digit;
      }
    });
    const next = characters.join("").slice(0, OTP_LENGTH);
    commit(next, Math.min(targetIndex + pasted.length, OTP_LENGTH - 1));
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusBox(index - 1);
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      focusBox(index + 1);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      focusBox(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      focusBox(Math.min(code.length, OTP_LENGTH - 1));
      return;
    }
    if (event.key !== "Backspace" && event.key !== "Delete") {
      return;
    }

    event.preventDefault();
    if (index < code.length) {
      commit(code.slice(0, index), index);
      return;
    }

    const previousIndex = Math.max(0, Math.min(index - 1, code.length - 1));
    commit(code.slice(0, previousIndex), previousIndex);
  };

  return (
    <div
      className={["lz-otp-input", error && "lz-otp-input--invalid", className]
        .filter(Boolean)
        .join(" ")}
      role="group"
      aria-label={ariaLabel}
    >
      {Array.from({ length: OTP_LENGTH }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          className="lz-otp-input__box"
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          pattern="[0-9]*"
          maxLength={OTP_LENGTH}
          value={code[index] || ""}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
          aria-invalid={error || undefined}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
        />
      ))}
    </div>
  );
}

export { OTP_LENGTH, digitsOnly };
